import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "@/components/Reveal";

const METHOD_IMG =
  "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZWRpdG9yaWFsJTIwaW50ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2MDU4NDUxfDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  {
    n: "01",
    title: "Audit",
    body: "We map everything — offers, clients, tools, hours, energy. Most founders have never seen their whole business on one page. It is equal parts horrifying and liberating.",
  },
  {
    n: "02",
    title: "Subtract",
    body: "We cut the offers that drain you, the clients who cost more than they pay, and the busywork disguised as productivity. Subtraction is the highest-leverage move nobody teaches.",
  },
  {
    n: "03",
    title: "Systemize",
    body: "What remains gets a system: delivery, sales, money, and your week. Simple enough to run from a phone. Strong enough to run without you.",
  },
  {
    n: "04",
    title: "Sustain",
    body: "We design your growth around your energy, not against it. Boundaries, pricing, and pace — so the business compounds without consuming you.",
  },
];

export default function Method() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <main data-testid="method-page" className="pt-32 sm:pt-44">
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pb-24 sm:pb-32">
        <Overline className="mb-10">The Method</Overline>
        <h1 className="font-serif text-5xl sm:text-7xl font-light tracking-tighter leading-[0.95] text-ink max-w-4xl">
          <MaskedLine delay={0.2}>Four moves.</MaskedLine>
          <MaskedLine delay={0.35} className="italic text-sage">
            No hustle required.
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 text-base sm:text-lg leading-relaxed text-neutral-600 max-w-xl"
        >
          This isn't a framework from a business book. It's the sequence I've used with
          120+ founders to turn chaotic, founder-dependent businesses into calm machines.
        </motion.p>
      </section>

      <section ref={imgRef} className="max-w-[1400px] mx-auto px-6 sm:px-10 pb-32">
        <FadeUp className="lg:ml-auto lg:w-2/3 overflow-hidden">
          <motion.img
            src={METHOD_IMG}
            alt="A calm, minimal interior with an archway"
            data-testid="method-image"
            style={{ y }}
            className="w-full aspect-[16/9] object-cover will-change-transform"
          />
        </FadeUp>
      </section>

      <section data-testid="method-steps-section" className="bg-bone py-32 sm:py-40">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 flex flex-col gap-28 sm:gap-36">
          {STEPS.map((s, i) => (
            <FadeUp key={s.n} delay={0.05 * i}>
              <div
                data-testid={`method-step-${s.n}`}
                className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start ${
                  i % 2 === 1 ? "md:pl-24" : ""
                }`}
              >
                <span className="md:col-span-3 font-serif text-7xl sm:text-8xl font-light text-clay/70 leading-none">
                  {s.n}
                </span>
                <div className="md:col-span-7">
                  <h2 className="font-serif text-3xl sm:text-5xl font-light text-ink mb-6">{s.title}</h2>
                  <p className="text-base sm:text-lg leading-relaxed text-neutral-600 max-w-xl">{s.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="py-32 sm:py-40">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <FadeUp className="flex flex-col sm:flex-row sm:items-center justify-between gap-10">
            <h2 className="font-serif text-4xl sm:text-6xl font-light tracking-tight text-ink max-w-2xl text-balance">
              Ready to see your business on one page?
            </h2>
            <Link
              to="/contact"
              data-testid="method-cta-button"
              className="group w-fit shrink-0 rounded-full bg-sage text-sand px-8 py-4 text-sm tracking-wide hover:bg-ink transition-colors duration-300 flex items-center gap-3"
            >
              Book a Chaos Audit
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
